import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import CampaignPicker from './CampaignPicker';
import Pagination from '../Pagination';
import CatalogImportDetail from './CatalogImportDetail';
import { errorMessage, getAiUsage, listImports, uploadCatalog } from '../../api/catalogImportApi';
import { IMPORT_STATUS, btnPrimary, card, h1, input, label, muted } from './styles';

const MAX_MB = 50; // spring.servlet.multipart.max-file-size on the backend
const POLL_MS = 5000;

function UploadCard({ onUploaded, initialTarget }) {
  const [file, setFile] = useState(null);
  const [defaults, setDefaults] = useState({ sellerName: '', sellerContactInformation: '', spoc: '' });
  const [target, setTarget] = useState(initialTarget);
  const [pickPages, setPickPages] = useState(false); // opt-in: reading starts straight away by default
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState('');

  const pick = (e) => {
    const f = e.target.files?.[0] || null;
    setError('');
    if (f && f.size > MAX_MB * 1024 * 1024) {
      setError(`That file is ${(f.size / 1024 / 1024).toFixed(1)}MB; the limit is ${MAX_MB}MB. Split the catalog and upload it in parts.`);
      setFile(null);
      e.target.value = '';
      return;
    }
    setFile(f);
  };

  const upload = async () => {
    if (!file) return;
    if (target.companyId && !target.campaignId) {
      setError('Choose a campaign for that company, or switch back to "Main catalogue only".');
      return;
    }
    setError('');
    setProgress(0);
    try {
      const res = await uploadCatalog(file, { ...defaults, campaignId: target.campaignId || undefined, pickPages }, (ev) => ev.total && setProgress(Math.round((ev.loaded / ev.total) * 100)));
      onUploaded(res.data.id);
    } catch (err) {
      setError(err?.response?.status === 413
        ? `That file is over the server's upload limit.`
        : errorMessage(err, 'Upload failed. Please try again.'));
      setProgress(null);
    }
  };

  const setDefault = (k) => (e) => setDefaults((d) => ({ ...d, [k]: e.target.value }));
  const uploading = progress !== null;

  return (
    <section style={card} aria-label="Upload a catalog">
      <h2 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 700 }}>Upload a supplier catalog</h2>
      <p style={{ ...muted, marginBottom: 16 }}>
        AI reads every page and drafts the products with their photos. Nothing is added to the catalogue until you review and create each one.
      </p>
      {error && <div role="alert" style={{ color: '#B91C1C', fontSize: 13, marginBottom: 12 }}>{error}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={label} htmlFor="catalog-file">Catalog PDF (up to {MAX_MB}MB)</label>
          <input id="catalog-file" type="file" accept="application/pdf,.pdf" onChange={pick} disabled={uploading} style={input} />
        </div>
        <CampaignPicker value={target} onChange={setTarget} disabled={uploading} />
        <div>
          <label style={label} htmlFor="catalog-seller">Seller (applies to every product)</label>
          <input id="catalog-seller" style={input} value={defaults.sellerName} onChange={setDefault('sellerName')} disabled={uploading} />
        </div>
        <div>
          <label style={label} htmlFor="catalog-seller-contact">Seller contact</label>
          <input id="catalog-seller-contact" style={input} value={defaults.sellerContactInformation} onChange={setDefault('sellerContactInformation')} disabled={uploading} />
        </div>
        <div>
          <label style={label} htmlFor="catalog-spoc">SPOC</label>
          <input id="catalog-spoc" style={input} value={defaults.spoc} onChange={setDefault('spoc')} disabled={uploading} />
        </div>
      </div>
      <p style={{ ...muted, fontSize: 12, marginTop: 10 }}>GST is set per product during review — it varies by product.</p>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#374151', marginTop: 10, cursor: 'pointer' }}>
        <input type="checkbox" checked={pickPages} onChange={(e) => setPickPages(e.target.checked)} disabled={uploading}
               style={{ width: 16, height: 16, accentColor: '#F29213' }} />
        Let me choose which pages to read first <span style={{ color: '#9CA3AF' }}>(skip covers and branding pages to save AI cost — reading starts by itself with all pages if you don’t choose within 10 minutes)</span>
      </label>

      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12, marginTop: 12 }}>
        {uploading && <span style={muted}>Uploading… {progress}%</span>}
        <button style={{ ...btnPrimary, opacity: !file || uploading ? 0.6 : 1 }} disabled={!file || uploading} onClick={upload}>
          Upload & read catalog
        </button>
      </div>
    </section>
  );
}

/** Admin view: upload catalogs, see every import, open one to review its drafts. */
export default function CatalogImportManagement() {
  // Arriving from a Brandstore campaign's "Import catalog" button: /admin?tab=CatalogImport&company=..&campaign=..
  const [searchParams] = useSearchParams();
  const initialTarget = { companyId: searchParams.get('company') || '', campaignId: searchParams.get('campaign') || '' };
  const [openId, setOpenId] = useState(null);
  const [imports, setImports] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [usage, setUsage] = useState(null);

  const load = useCallback(() => {
    getAiUsage().then((r) => setUsage(r.data)).catch(() => {});
    listImports(page, 20)
      .then((r) => { setImports(r.data.content || []); setTotalPages(r.data.totalPages || 0); setLoadError(''); })
      .catch((err) => setLoadError(errorMessage(err, 'Could not load imports.')))
      .finally(() => setLoading(false));
  }, [page]);

  useEffect(() => { if (openId === null) load(); }, [load, openId]);

  const anyActive = imports.some((i) => i.running || i.status === 'UPLOADED' || i.status === 'PROCESSING');
  useEffect(() => {
    if (openId !== null || !anyActive) return undefined;
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [openId, anyActive, load]);

  if (openId !== null) {
    return <CatalogImportDetail importId={openId} onBack={() => setOpenId(null)} />;
  }

  return (
    <div style={{ padding: '24px 0' }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={h1}>Import from Catalog</h1>
        <p style={muted}>Turn a supplier's PDF catalog into products — AI drafts them, you review and create.</p>
        {usage && (
          <p style={{ ...muted, marginTop: 6, color: usage.monthlyBudgetUsd && usage.monthSpendUsd >= usage.monthlyBudgetUsd ? '#B91C1C' : '#6B7280' }}>
            AI spend this month ≈ ${usage.monthSpendUsd.toFixed(2)}
            {usage.monthlyBudgetUsd ? ` of the $${usage.monthlyBudgetUsd.toFixed(2)} monthly budget` : ' (no monthly budget set)'}
          </p>
        )}
      </div>

      <UploadCard onUploaded={setOpenId} initialTarget={initialTarget} />

      <h2 style={{ fontSize: 16, fontWeight: 700, margin: '24px 0 12px' }}>Imports</h2>
      {loadError && <p role="alert" style={{ color: '#B91C1C', fontSize: 13 }}>{loadError}</p>}
      {loading ? (
        <p style={{ color: '#9CA3AF' }}>Loading…</p>
      ) : imports.length === 0 ? (
        <p style={{ color: '#9CA3AF' }}>No catalogs imported yet.</p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {imports.map((imp) => {
            const s = IMPORT_STATUS[imp.status] || IMPORT_STATUS.UPLOADED;
            const c = imp.itemCounts || {};
            return (
              <li key={imp.id}>
                <button onClick={() => setOpenId(imp.id)}
                        style={{ ...card, width: '100%', textAlign: 'left', cursor: 'pointer', display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', padding: '14px 18px', marginBottom: 10, font: 'inherit' }}>
                  <span style={{ flex: '1 1 240px', minWidth: 0 }}>
                    <strong style={{ display: 'block', fontSize: 14, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{imp.fileName}</strong>
                    <span style={{ fontSize: 12, color: '#9CA3AF' }}>
                      {imp.createdBy} · {imp.createdAt ? new Date(imp.createdAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : ''}
                      {imp.campaign && <> · 🏪 {imp.campaign.companyName ? `${imp.campaign.companyName} — ` : ''}{imp.campaign.name}</>}
                    </span>
                  </span>
                  <span style={s.style}>{s.text}</span>
                  <span style={{ fontSize: 12, color: '#6B7280' }}>
                    {imp.pageCount > 0 ? `${imp.pagesProcessed}/${imp.pageCount} pages` : '—'}
                  </span>
                  <span style={{ fontSize: 12, color: '#6B7280' }}>
                    {c.PENDING_REVIEW || 0} to review · {c.CREATED || 0} created{c.LINKED ? ` · ${c.LINKED} existing` : ''}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {totalPages > 1 && <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />}
    </div>
  );
}
